import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, AlertTriangle, Phone, Stethoscope, Users, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { BackToTopButton } from '../components/NavigationHelpers';
import { Button } from '../components/ui/button';

/**
 * RelapseRecoveryPage — placeholder.
 * Full urine drug-screen tracking + court-report integration will be built in a later session
 * (see ROADMAP.md).
 *
 * Court-report wording principle: STRICTLY FACTUAL, NO AI commentary.
 *   ✓ "Engaged fully with all ARP programme material"
 *   ✓ "Urine drug screens collected on dates X, Y, Z — all clear"
 *   ✗ "Made excellent progress" / "demonstrated remorse" / etc.
 */
function RelapseRecoveryPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-100">
      <PageHeader title="Relapse Recovery" />
      <BackToTopButton />

      <main className="pt-20 pb-20 px-4">
        <div className="max-w-md mx-auto space-y-4">

          {/* Hero: compassionate, not punitive */}
          <div className="bg-white border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <LifeBuoy className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-zinc-900">If you have relapsed: you are not finished.</h2>
                <p className="text-[12px] text-zinc-600 mt-1 leading-relaxed">
                  Alcoholism/addiction is a chronic, relapsing disease. A relapse is a serious medical event,
                  but it does not undo the work you've done. The most important thing right now is your safety.
                </p>
              </div>
            </div>
          </div>

          {/* Step 1: Get medical safety */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
              <h3 className="text-sm font-bold text-zinc-900">Get medically safe: first.</h3>
            </div>
            <p className="text-[12px] text-zinc-600 leading-relaxed mb-3">
              Withdrawal from alcohol or some drugs (benzodiazepines, opioids) can be life-threatening.
              Do not detox alone.
            </p>
            <ul className="text-[12px] text-zinc-700 space-y-2 mb-3">
              <li className="flex gap-2"><Phone className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" /> <span><strong>Emergency:</strong> 000 (Australia), 911 (US/Canada), 112 (EU/UK).</span></li>
              <li className="flex gap-2"><Stethoscope className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> <span><strong>Speak to a doctor or hospital</strong> about supervised detox before you do anything else.</span></li>
              <li className="flex gap-2"><Users className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /> <span><strong>Tell your sponsor.</strong> Honesty is the lifeline of Recovery.</span></li>
            </ul>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/emergency-support')}
              data-testid="emergency-support-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Emergency Support
            </Button>
          </div>

          {/* Step 2: Urine Drug Screen: placeholder */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 opacity-90">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
              <h3 className="text-sm font-bold text-zinc-900">Urine Drug Screen (UDS)</h3>
              <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">Coming soon</span>
            </div>
            <p className="text-[12px] text-zinc-600 leading-relaxed mb-2">
              Returning to the programme requires a fresh urine drug screen so your case manager and (if relevant)
              the Court can see verified clean evidence going forward.
            </p>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              In <strong>Australia</strong>, your GP can refer you for a urine drug screen. Ask your GP
              or the pathology centre whether there is any cost.
              Other countries: ask your prescribing clinician for a referral to an accredited toxicology lab.
            </p>
          </div>

          {/* Step 3: Re-engage with the programme */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
              <h3 className="text-sm font-bold text-zinc-900">Re-engage: gently and consistently.</h3>
            </div>
            <p className="text-[12px] text-zinc-600 leading-relaxed mb-3">
              Recovery from a relapse means going back to the basics. The programme works when you work it,
              even when you don't feel like it.
            </p>
            <ul className="text-[12px] text-zinc-700 space-y-1.5">
              <li>• Attend a meeting today. In person if at all possible.</li>
              <li>• Re-read the <strong>Disease Module</strong> in the 2-Week Window.</li>
              <li>• Resume daily readings and lessons from your current week.</li>
              <li>• Reach out to your sponsor: daily calls for the first week.</li>
              <li>• Use the Recovery Room to ask for support; you're not alone.</li>
            </ul>
          </div>

          {/* Back to Profile */}
          <Button
            variant="outline"
            className="w-full h-12"
            onClick={() => navigate('/profile')}
            data-testid="back-to-profile-btn"
          >
            <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
            Back to Profile
          </Button>
        </div>
      </main>
    </div>
  );
}

export default RelapseRecoveryPage;
